import { FC, useCallback } from 'react';

import Button from 'components/_core/Button';
import { SunshineModalProps } from 'components/_core/SunshineModal';
import useToast from 'components/ToastProvider/useToast';

import { CatalogItemFormSchema } from '../../catalogItemForm.schema';
import BaseModal from '../BaseModal';
import useCreateCatalogItemMutation from './hooks/useCreateCatalogItemMutation';
import locales from './locales';

interface CreateModalProps extends Omit<SunshineModalProps, 'children'> {
  initialValues?: CatalogItemFormSchema;
}

const CreateModal: FC<CreateModalProps> = ({
  initialValues,
  isOpen,
  onClose,
  ...modalProps
}) => {
  const { showToast } = useToast();

  const [createCatalogItem, { loading }] = useCreateCatalogItemMutation();

  const onSave = useCallback(
    async (values: CatalogItemFormSchema) => {
      const { errors } = await createCatalogItem({
        ...values,
        subtitle: values.subtitle?.trim(),
      });

      if (errors) {
        showToast({ message: locales.errorMessage, type: 'error' });
        return;
      }

      showToast({ message: locales.successMessage, type: 'success' });
      onClose();
    },
    [createCatalogItem, showToast, onClose],
  );

  return (
    <BaseModal
      {...modalProps}
      description={locales.description}
      handleSave={onSave}
      initialValues={initialValues}
      isLoading={loading}
      isOpen={isOpen}
      onClose={onClose}
      title={locales.title}
    >
      <Button onClick={onClose} variant="inline-secondary">
        {locales.cancel}
      </Button>
    </BaseModal>
  );
};

export default CreateModal;
